import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, MapPin, Clock, Calendar, Users, CheckCircle2,
  AlertCircle, Navigation, Truck, Filter
} from "lucide-react";

const statusColors = {
  pending: "bg-slate-100 text-slate-700", dispatched: "bg-blue-100 text-blue-700",
  en_route: "bg-amber-100 text-amber-700", on_site: "bg-purple-100 text-purple-700",
  completed: "bg-emerald-100 text-emerald-700", cancelled: "bg-red-100 text-red-700",
};
const statusLabels = {
  pending: "Pending", dispatched: "Dispatched", en_route: "En Route",
  on_site: "On Site", completed: "Completed", cancelled: "Cancelled",
};
const priorityColors = {
  low: "bg-slate-100 text-slate-700", medium: "bg-blue-100 text-blue-700",
  high: "bg-amber-100 text-amber-700", urgent: "bg-red-100 text-red-700",
};

export default function WorkOrderManagement() {
  const navigate = useNavigate();
  const [workOrders, setWorkOrders] = useState([]);
  const [agents, setAgents] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState({
    title: "", description: "", client_id: "", location_address: "",
    scheduled_date: new Date().toISOString().split("T")[0],
    estimated_duration_hours: "2", priority: "medium", product_id: "",
  });

  const fetchData = useCallback(async () => {
    try {
      const [woRes, agentsRes, clientsRes, productsRes] = await Promise.all([
        apiClient.get("/work-orders"),
        apiClient.get("/users/field-agents"),
        apiClient.get("/users?role=client"),
        apiClient.get("/products"),
      ]);
      setWorkOrders(woRes.data);
      setAgents(agentsRes.data);
      setClients(clientsRes.data);
      setProducts(productsRes.data);
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- apiClient is module-level stable, state setters are stable
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post("/work-orders", {
        ...form,
        estimated_duration_hours: parseFloat(form.estimated_duration_hours),
        product_id: form.product_id === "none" ? null : form.product_id || null,
      });
      toast.success("Work order created");
      setCreateOpen(false);
      setForm({ title: "", description: "", client_id: "", location_address: "", scheduled_date: new Date().toISOString().split("T")[0], estimated_duration_hours: "2", priority: "medium", product_id: "" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create work order");
    }
  };

  const handleAssign = async (woId, agentId) => {
    try {
      await apiClient.patch(`/work-orders/${woId}`, {
        assigned_agent_id: agentId,
        status: "dispatched",
        status_note: `Dispatched to field agent`,
      });
      toast.success("Agent assigned & dispatched");
      fetchData();
    } catch {
      toast.error("Failed to assign agent");
    }
  };

  const filteredOrders = workOrders.filter((wo) => {
    if (filter === "all") return true;
    if (filter === "active") return !["completed", "cancelled"].includes(wo.status);
    return wo.status === filter;
  });

  const stats = {
    total: workOrders.length,
    active: workOrders.filter(w => !["completed", "cancelled"].includes(w.status)).length,
    completed: workOrders.filter(w => w.status === "completed").length,
    unassigned: workOrders.filter(w => !w.assigned_agent_id && w.status === "pending").length,
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="work-order-management-page">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/admin")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-heading text-2xl font-bold text-slate-900">Field Service Dispatch</h1>
            <p className="text-slate-500 mt-1">Create, assign, and track field service work orders</p>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-sag-navy hover:bg-sag-navy-800" data-testid="create-work-order-button">
                <Plus className="h-4 w-4 mr-2" /> New Work Order
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Create Work Order</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Title *</Label>
                  <Input placeholder="e.g., Server rack installation" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="wo-title-input" />
                </div>
                <div className="space-y-2">
                  <Label>Description *</Label>
                  <Textarea placeholder="Job details..." value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} required />
                </div>
                <div className="space-y-2">
                  <Label>Client *</Label>
                  <Select value={form.client_id || "placeholder"} onValueChange={(v) => setForm({ ...form, client_id: v === "placeholder" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="Select client..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="placeholder" disabled>Select client...</SelectItem>
                      {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Location Address *</Label>
                  <Input placeholder="123 Main St, City, Country" value={form.location_address} onChange={(e) => setForm({ ...form, location_address: e.target.value })} required data-testid="wo-location-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Scheduled Date *</Label>
                    <Input type="date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Estimated Hours</Label>
                    <Input type="number" step="0.5" min="0.5" value={form.estimated_duration_hours} onChange={(e) => setForm({ ...form, estimated_duration_hours: e.target.value })} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Priority</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Product</Label>
                    <Select value={form.product_id || "none"} onValueChange={(v) => setForm({ ...form, product_id: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-sag-navy hover:bg-sag-navy-800" disabled={!form.client_id || !form.title || !form.location_address} data-testid="submit-work-order">
                    Create Work Order
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="sag-card"><CardContent className="p-5 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-sag-navy/10 text-sag-navy"><Truck className="h-6 w-6" /></div>
            <div><p className="text-2xl font-bold font-heading">{stats.total}</p><p className="text-sm text-slate-500">Total Orders</p></div>
          </CardContent></Card>
          <Card className="sag-card"><CardContent className="p-5 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-blue-100 text-blue-600"><Navigation className="h-6 w-6" /></div>
            <div><p className="text-2xl font-bold font-heading">{stats.active}</p><p className="text-sm text-slate-500">Active</p></div>
          </CardContent></Card>
          <Card className="sag-card"><CardContent className="p-5 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-emerald-100 text-emerald-600"><CheckCircle2 className="h-6 w-6" /></div>
            <div><p className="text-2xl font-bold font-heading">{stats.completed}</p><p className="text-sm text-slate-500">Completed</p></div>
          </CardContent></Card>
          <Card className="sag-card"><CardContent className="p-5 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-amber-100 text-amber-600"><AlertCircle className="h-6 w-6" /></div>
            <div><p className="text-2xl font-bold font-heading">{stats.unassigned}</p><p className="text-sm text-slate-500">Unassigned</p></div>
          </CardContent></Card>
        </div>

        {/* Filter + Table */}
        <Card className="sag-card">
          <CardHeader className="border-b border-slate-100 flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg">Work Orders</CardTitle>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-[150px]"><Filter className="h-4 w-4 mr-2" /><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="dispatched">Dispatched</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Loading...</div>
            ) : filteredOrders.length === 0 ? (
              <div className="p-8 text-center">
                <MapPin className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No work orders found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Work Order</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Scheduled</TableHead>
                    <TableHead>Assigned To</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrders.map((wo) => (
                    <TableRow key={wo.id} data-testid={`wo-row-${wo.id}`} className="cursor-pointer hover:bg-slate-50"
                      onClick={() => navigate(`/work-orders/${wo.id}`)}>
                      <TableCell>
                        <div className="font-medium text-slate-900">{wo.title}</div>
                        <div className="text-xs text-slate-500">#{wo.id.slice(0, 8)}</div>
                      </TableCell>
                      <TableCell className="text-slate-700">{wo.client_name}</TableCell>
                      <TableCell className="text-slate-600 max-w-[150px] truncate">
                        <div className="flex items-center gap-1"><MapPin className="h-3 w-3 shrink-0" />{wo.location_address}</div>
                      </TableCell>
                      <TableCell className="text-slate-600">
                        <div className="flex items-center gap-1"><Calendar className="h-3 w-3" />{new Date(wo.scheduled_date).toLocaleDateString()}</div>
                      </TableCell>
                      <TableCell>
                        {wo.assigned_agent_name ? (
                          <div className="flex items-center gap-1 text-slate-700"><Users className="h-3 w-3" />{wo.assigned_agent_name}</div>
                        ) : wo.status === "pending" ? (
                          <Select onValueChange={(agentId) => { handleAssign(wo.id, agentId); }}
                            value="placeholder">
                            <SelectTrigger className="h-8 w-[140px] text-xs" onClick={(e) => e.stopPropagation()}>
                              <SelectValue placeholder="Assign agent..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="placeholder" disabled>Assign agent...</SelectItem>
                              {agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.full_name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="text-slate-400 text-sm">Unassigned</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[wo.status]} variant="secondary">{statusLabels[wo.status]}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={priorityColors[wo.priority]} variant="secondary">{wo.priority}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
